import React, { useState } from 'react';
import { useSelector } from 'react-redux';

import { updateApplicationCurrentFlow } from 'thunks';
import ActionCard from 'components/Common/Cards/ActionCard';
import { getApplicationData } from 'selectors/getApplicationData';
import { getLoanGoal } from 'selectors/getLoanGoal';
import { RoutePath } from 'enums/Routes';
import { useNavigate } from 'hooks/useNavigate';
import { CurrentFlow } from 'enums/CurrentFlow';
import useOpenAiCoachConversationChat from 'hooks/useOpenAiCoachConversationChat';
import { hasPrimaryGoalCompleted, hasSelectedAICoachGoals } from 'utils/aiCoachUtils';
import { FROM_FINANCIAL_DASHBOARD_STATE, OFFER_AVAILABLE_FROM_DASHBOARD_STATE } from 'utils/navigationStates';

import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';

import { mapAppStatusToActionCardProps } from './resolveNextStepActionCard';

import styles from './NextStepSection.module.scss';

const NextStepSection: React.FC = () => {
  const { application } = useSelector(getApplicationData);
  const { aiCoachGoals } = useSelector(getLoanGoal);
  const [isLoadingNextStep, setIsLoadingNextStep] = useState(false);
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const openAiCoachConversationChat = useOpenAiCoachConversationChat();

  if (!application) return null;

  const nextStepCardProps = mapAppStatusToActionCardProps(application, aiCoachGoals || []);

  if (!nextStepCardProps) return null;

  const { title, description, buttonText, estimatedDuration, route, chip, isLoading } = nextStepCardProps;

  const handleNextStepClick = async () => {
    if (route === RoutePath.AICoachGoals) {
      // If user already has goals, open the AI Coach conversation workflow
      if (hasSelectedAICoachGoals(aiCoachGoals) && !hasPrimaryGoalCompleted(aiCoachGoals)) {
        openAiCoachConversationChat();
        return;
      }

      // Change flow to AICoach and navigate to AICoachGoals
      if (application.currentFlow !== CurrentFlow.AICoach) {
        setIsLoadingNextStep(true);
        try {
          await dispatchWithUnwrap(
            updateApplicationCurrentFlow({
              applicationId: application.id,
              currentFlow: CurrentFlow.AICoach,
            }),
          );
        } finally {
          setIsLoadingNextStep(false);
        }
      }

      navigate(RoutePath.AICoachGoals, FROM_FINANCIAL_DASHBOARD_STATE);
      return;
    }

    if (route) navigate(route, route === RoutePath.OfferAvailable ? OFFER_AVAILABLE_FROM_DASHBOARD_STATE : undefined);
  };

  return (
    <div className={styles.nextStepSection}>
      <p className={styles.sectionLabel}>Next step</p>
      <ActionCard>
        {chip}
        <div>
          {estimatedDuration && <ActionCard.Duration>{estimatedDuration}</ActionCard.Duration>}
          <ActionCard.Title>{title}</ActionCard.Title>
          {description && <ActionCard.Description>{description}</ActionCard.Description>}
        </div>
        {buttonText && (
          <ActionCard.Button onClick={handleNextStepClick} isLoading={isLoading || isLoadingNextStep}>
            {buttonText}
          </ActionCard.Button>
        )}
      </ActionCard>
    </div>
  );
};

export default NextStepSection;
